import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Quote } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import { SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { useContacts, useCreateContact } from '../contacts/api.js';
import { useUpdateOpportunity } from '../opportunities/api.js';
import { useDisableQuoteDeliveryLink, useRegenerateQuoteDeliveryLink, useSendQuoteEmail } from './api.js';

interface SendQuoteEmailModalProps {
  isOpen: boolean;
  quote: Quote;
  onClose: () => void;
}

function quoteUrlOf(quote: Quote): string {
  if (quote.deliveryToken && quote.deliveryEnabled && typeof window !== 'undefined') {
    return `${window.location.origin}/q/${quote.deliveryToken}`;
  }
  return '';
}

function buildDefaultBody(firstName: string, url: string, opportunityName: string): string {
  const greeting = firstName ? `Hi ${firstName},` : 'Hi,';
  const link = url || '[generate the quote link first to insert it here]';
  return `${greeting}

Thank you for the opportunity to work together on ${opportunityName}. Your quote is ready to review at the link below:

${link}

Please take a look and let us know if you have any questions — we're happy to walk through any part of it with you.

Best,
Catalyst`;
}

export function SendQuoteEmailModal({
  isOpen,
  quote,
  onClose,
}: SendQuoteEmailModalProps): JSX.Element {
  const queryClient = useQueryClient();
  const sendMutation = useSendQuoteEmail();
  const regenMutation = useRegenerateQuoteDeliveryLink();
  const disableMutation = useDisableQuoteDeliveryLink();
  const updateOpportunity = useUpdateOpportunity();
  const createContact = useCreateContact();
  const createCcContact = useCreateContact();
  const resetSend = sendMutation.reset;
  const resetRegen = regenMutation.reset;
  const resetUpdateOpportunity = updateOpportunity.reset;
  const resetCreateContact = createContact.reset;
  const resetCreateCcContact = createCcContact.reset;

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [copied, setCopied] = useState(false);
  const [showNewContact, setShowNewContact] = useState(false);
  const [newFirst, setNewFirst] = useState('');
  const [newLast, setNewLast] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [showNewCcContact, setShowNewCcContact] = useState(false);
  const [ccFirst, setCcFirst] = useState('');
  const [ccLast, setCcLast] = useState('');
  const [ccEmail, setCcEmail] = useState('');

  const contactsQuery = useContacts(
    { customerId: quote.customerId, isActive: true, pageSize: 200 },
    isOpen,
  );
  const contacts = contactsQuery.data?.data ?? [];

  const quoteUrl = quoteUrlOf(quote);
  const hasLink = Boolean(quoteUrl);

  useEffect(() => {
    if (!isOpen) return;
    const firstName = (quote.contactName ?? '').split(' ')[0] ?? '';
    setTo(quote.contactEmail ?? '');
    setCc('');
    setSubject(`Your quote from Catalyst — ${quote.opportunityName}`);
    setBody(buildDefaultBody(firstName, quoteUrlOf(quote), quote.opportunityName));
    setCopied(false);
    setShowNewContact(false);
    setNewFirst('');
    setNewLast('');
    setNewEmail('');
    setShowNewCcContact(false);
    setCcFirst('');
    setCcLast('');
    setCcEmail('');
    resetSend();
    resetRegen();
    resetUpdateOpportunity();
    resetCreateContact();
    resetCreateCcContact();
    // Only reset on open / quote change (avoids clobbering edits when the link is generated).
  }, [isOpen, quote.id, resetSend, resetRegen, resetUpdateOpportunity, resetCreateContact, resetCreateCcContact]);

  // The To field is read-only and always mirrors the opportunity contact.
  useEffect(() => {
    if (isOpen) setTo(quote.contactEmail ?? '');
  }, [isOpen, quote.contactEmail]);

  /** Points the opportunity at a different contact so it always matches the recipient. */
  const applyContact = (contactId: string, firstName: string, email: string | null): void => {
    updateOpportunity.mutate(
      { id: quote.opportunityId, input: { contactId } },
      {
        onSuccess: () => {
          setTo(email ?? '');
          // Only rewrite the greeting when the message is still the untouched default.
          const currentFirst = (quote.contactName ?? '').split(' ')[0] ?? '';
          const currentDefault = buildDefaultBody(currentFirst, quoteUrl, quote.opportunityName);
          if (body === currentDefault) {
            setBody(buildDefaultBody(firstName, quoteUrl, quote.opportunityName));
          }
          void queryClient.invalidateQueries({ queryKey: ['quotes'] });
        },
      },
    );
  };

  const handleContactChange = (contactId: string): void => {
    if (!contactId) return;
    const contact = contacts.find((c) => c.id === contactId);
    if (!contact) return;
    applyContact(contact.id, contact.firstName, contact.email);
  };

  const canCreateContact = Boolean(newFirst.trim() && newLast.trim() && newEmail.trim());

  /** Appends an email to the freeform CC field (comma-separated, deduped). */
  const appendCc = (email: string | null): void => {
    const trimmed = (email ?? '').trim();
    if (!trimmed) return;
    setCc((prev) => {
      const parts = prev
        .split(/[,;]+/)
        .map((p) => p.trim())
        .filter(Boolean);
      if (parts.some((p) => p.toLowerCase() === trimmed.toLowerCase())) return prev;
      return [...parts, trimmed].join(', ');
    });
  };

  const handleCcContactPick = (contactId: string): void => {
    if (!contactId) return;
    const contact = contacts.find((c) => c.id === contactId);
    if (!contact) return;
    appendCc(contact.email);
  };

  const canCreateCcContact = Boolean(ccFirst.trim() && ccLast.trim() && ccEmail.trim());

  const handleCreateCcContact = (): void => {
    if (!canCreateCcContact) return;
    createCcContact.mutate(
      {
        customerId: quote.customerId,
        firstName: ccFirst.trim(),
        lastName: ccLast.trim(),
        email: ccEmail.trim(),
      },
      {
        onSuccess: (created) => {
          setShowNewCcContact(false);
          setCcFirst('');
          setCcLast('');
          setCcEmail('');
          appendCc(created.email);
        },
      },
    );
  };

  const handleCreateContact = (): void => {
    if (!canCreateContact) return;
    createContact.mutate(
      {
        customerId: quote.customerId,
        firstName: newFirst.trim(),
        lastName: newLast.trim(),
        email: newEmail.trim(),
      },
      {
        onSuccess: (created) => {
          setShowNewContact(false);
          setNewFirst('');
          setNewLast('');
          setNewEmail('');
          applyContact(created.id, created.firstName, created.email);
        },
      },
    );
  };

  const handleGenerateLink = (): void => {
    regenMutation.mutate(quote.id, {
      onSuccess: (updated) => {
        const url = updated.deliveryToken
          ? `${window.location.origin}/q/${updated.deliveryToken}`
          : '';
        const firstName = (updated.contactName ?? '').split(' ')[0] ?? '';
        setBody(buildDefaultBody(firstName, url, updated.opportunityName));
      },
    });
  };

  const handleCopy = (): void => {
    void navigator.clipboard.writeText(quoteUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSend = (): void => {
    sendMutation.mutate(
      {
        id: quote.id,
        input: { to: to.trim(), cc: cc.trim() || null, subject: subject.trim(), body },
      },
      { onSuccess: () => onClose() },
    );
  };

  const error =
    sendMutation.error instanceof ApiError
      ? sendMutation.error.message
      : sendMutation.error
        ? 'Failed to send the quote email.'
        : null;

  const contactError =
    updateOpportunity.error instanceof ApiError
      ? updateOpportunity.error.message
      : updateOpportunity.error
        ? 'Failed to update the opportunity contact.'
        : createContact.error instanceof ApiError
          ? createContact.error.message
          : createContact.error
            ? 'Failed to create the contact.'
            : null;

  const contactBusy = updateOpportunity.isPending || createContact.isPending;

  const ccContactError =
    createCcContact.error instanceof ApiError
      ? createCcContact.error.message
      : createCcContact.error
        ? 'Failed to create the CC contact.'
        : null;

  const canSend = Boolean(to.trim() && subject.trim() && body.trim() && hasLink);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Send quote to customer"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={sendMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSend} isLoading={sendMutation.isPending} disabled={!canSend}>
            Send email
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-500">
          The customer opens the quote at a private link — every visit is tracked on the quote.
          Sending marks the quote as Sent.
        </p>

        {hasLink ? (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
            <span className="text-slate-500">Quote link: </span>
            <span className="min-w-0 flex-1 break-all font-medium text-slate-800">{quoteUrl}</span>
            <Button variant="secondary" size="sm" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              isLoading={regenMutation.isPending}
              onClick={handleGenerateLink}
              title="Rotating the link invalidates the previously shared URL"
            >
              Rotate
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-rose-600 hover:bg-rose-50"
              isLoading={disableMutation.isPending}
              onClick={() => disableMutation.mutate(quote.id)}
            >
              Disable
            </Button>
          </div>
        ) : (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
            <p>No active quote link yet — generate one to include in the email.</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              isLoading={regenMutation.isPending}
              onClick={handleGenerateLink}
            >
              Generate quote link
            </Button>
          </div>
        )}

        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-slate-700">
              Send Quote to Opportunity contact
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowNewContact((v) => !v)}
              disabled={contactBusy}
            >
              {showNewContact ? 'Cancel' : '+ New contact'}
            </Button>
          </div>
          {!to.trim() ? (
            <p className="mt-1 text-xs text-rose-600">
              The opportunity has no contact email yet — choose or add a contact below.
            </p>
          ) : null}
          {showNewContact ? (
            <div className="mt-2 grid gap-3 sm:grid-cols-3">
              <TextField
                label="First name"
                value={newFirst}
                onChange={(e) => setNewFirst(e.target.value)}
              />
              <TextField
                label="Last name"
                value={newLast}
                onChange={(e) => setNewLast(e.target.value)}
              />
              <TextField
                label="Email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
              <div className="sm:col-span-3">
                <Button
                  size="sm"
                  isLoading={contactBusy}
                  disabled={!canCreateContact}
                  onClick={handleCreateContact}
                >
                  Add contact &amp; use as recipient
                </Button>
                <p className="mt-1 text-xs text-slate-500">
                  Adds the contact to {quote.customerName} and sets them as the opportunity
                  contact.
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-2">
              <SelectField
                label="Change opportunity contact"
                value={quote.contactId ?? ''}
                placeholder="Choose a contact…"
                options={contacts.map((c) => ({
                  value: c.id,
                  label: `${`${c.firstName} ${c.lastName ?? ''}`.trim()}${
                    c.email ? ` — ${c.email}` : ' — (no email)'
                  }`,
                }))}
                disabled={contactBusy || contacts.length === 0}
                onChange={(e) => handleContactChange(e.target.value)}
              />
              <p className="mt-1 text-xs text-slate-500">
                Changing this updates the opportunity&apos;s contact so it stays the same person
                as the quote recipient.
              </p>
            </div>
          )}
          {contactError ? <p className="mt-2 text-sm text-rose-600">{contactError}</p> : null}
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-slate-700">CC</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowNewCcContact((v) => !v)}
              disabled={createCcContact.isPending}
            >
              {showNewCcContact ? 'Cancel' : '+ New contact'}
            </Button>
          </div>
          {showNewCcContact ? (
            <div className="mt-2 grid gap-3 sm:grid-cols-3">
              <TextField
                label="First name"
                value={ccFirst}
                onChange={(e) => setCcFirst(e.target.value)}
              />
              <TextField
                label="Last name"
                value={ccLast}
                onChange={(e) => setCcLast(e.target.value)}
              />
              <TextField
                label="Email"
                type="email"
                value={ccEmail}
                onChange={(e) => setCcEmail(e.target.value)}
              />
              <div className="sm:col-span-3">
                <Button
                  size="sm"
                  isLoading={createCcContact.isPending}
                  disabled={!canCreateCcContact}
                  onClick={handleCreateCcContact}
                >
                  Add contact &amp; CC them
                </Button>
                <p className="mt-1 text-xs text-slate-500">
                  Adds the contact to {quote.customerName} and adds their email to the CC list.
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-2">
              <SelectField
                label="Add contact to CC"
                value=""
                placeholder="Choose a contact…"
                options={contacts
                  .filter((c) => Boolean(c.email))
                  .map((c) => ({
                    value: c.id,
                    label: `${`${c.firstName} ${c.lastName ?? ''}`.trim()} — ${c.email}`,
                  }))}
                disabled={createCcContact.isPending || contacts.length === 0}
                onChange={(e) => handleCcContactPick(e.target.value)}
              />
            </div>
          )}
          <div className="mt-2">
            <TextField
              label="CC emails"
              value={cc}
              placeholder="Type any addresses, separated by commas"
              onChange={(e) => setCc(e.target.value)}
            />
            <p className="mt-1 text-xs text-slate-500">
              Pick contacts above or type any email addresses — separate multiple with commas.
            </p>
          </div>
          {ccContactError ? <p className="mt-2 text-sm text-rose-600">{ccContactError}</p> : null}
        </div>

        <TextField
          label="Subject"
          value={subject}
          maxLength={300}
          onChange={(e) => setSubject(e.target.value)}
        />

        <TextAreaField
          label="Message"
          value={body}
          rows={10}
          onChange={(e) => setBody(e.target.value)}
        />

        {error ? <p className="text-sm text-rose-600">{error}</p> : null}
      </div>
    </Modal>
  );
}
